import { useEffect, useState } from 'react';
import { api, getToken } from '../api/client';
import { urlImpression } from '../api/config';
import { Bandeau, Carte, Squelette } from '../components/ui';
import { T, langue } from '../i18n';

/**
 * Le mode d'emploi, à l'écran.
 *
 * Même texte que le guide imprimé : les deux lisent le même fichier côté
 * serveur. Chacun ouvre d'abord celui de son poste ; les gérants peuvent lire
 * les autres, c'est utile quand on explique à quelqu'un au téléphone.
 */

interface Etape { titre: string; points: string[]; image?: string; attention?: string }
interface Guide { role: string; poste: string; pour: string; resume: string; etapes: Etape[] }

export default function Aide() {
  const [d, setD] = useState<{ monRole: string; guides: Guide[] } | null>(null);
  const [role, setRole] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ monRole: string; guides: Guide[] }>('/aide').then((r) => { setD(r); setRole(r.monRole); });
  }, []);

  if (!d) return <Squelette lignes={8} />;
  const guide = d.guides.find((g) => g.role === role) ?? d.guides[0]!;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{T.aide.titre(guide.poste)}</h1>
          <p>{T.aide.sousTitre(guide.pour)}</p>
        </div>
        <div className="actions">
          <a className="btn btn-p" target="_blank" rel="noreferrer"
             href={urlImpression(`/aide/${guide.role}/imprimer?langue=${langue}`, getToken())}>
            {T.aide.imprimer}
          </a>
        </div>
      </div>

      {d.guides.length > 1 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {d.guides.map((g) => (
            <button key={g.role} className={`btn btn-s ${g.role === guide.role ? 'btn-p' : ''}`}
                    onClick={() => setRole(g.role)}>
              {g.poste}{g.role === d.monRole ? T.aide.leVotre : ''}
            </button>
          ))}
        </div>
      )}

      <Bandeau type="info">{guide.resume}</Bandeau>

      <div style={{ display: 'grid', gap: 12, marginTop: 14 }}>
        {guide.etapes.map((e, i) => (
          <Carte key={e.titre} titre={`${i + 1}. ${e.titre}`}>
            <ol style={{ margin: 0, paddingLeft: 20 }}>
              {e.points.map((p) => <li key={p} style={{ marginBottom: 4 }}>{p}</li>)}
            </ol>
            {e.attention && (
              <div style={{ marginTop: 10 }}>
                <Bandeau type="warn" titre={T.aide.aRetenir}>{e.attention}</Bandeau>
              </div>
            )}
            {e.image && (
              <img src={`/aide/${e.image}`} alt={e.titre} loading="lazy"
                   style={{ display: 'block', width: '100%', marginTop: 12, borderRadius: 8, border: '1px solid var(--bord)' }} />
            )}
          </Carte>
        ))}
      </div>
    </>
  );
}
